import {
  Button,
  Checkbox,
  Combobox,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FAIcon,
  FieldError,
  FieldLabel,
  NumberInput,
  UserCombobox,
} from '@/components'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { SalarySettingCombobox } from '@/components/combobox/salary-setting-combobox'
import { Module } from '@/modules/companies/consts'
import { useModuleConfig } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ReactNode } from 'react'
import type { Control, UseFormReturn } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import type { ConditionKey, EffectKey } from './generation-modifier-builder'
import {
  CONDITION_KEYS,
  EFFECT_KEYS,
  addedEffect,
  amountRequired,
  clearedCondition,
  clearedEffect,
  conditionAvailable,
  conditionIsSet,
  effectAvailable,
  effectIsSet,
} from './generation-modifier-builder'
import type { GenerationModifierForm } from './generation-modifier-form'
import { REST_TYPES } from './generation-modifier-form'
import { useGenerationRuleLabels } from './generation-rule-labels'
import { SALARY_ITEM_GENERATION_AREAS_QUERY } from './queries'
import { countryName } from '@/utils/country-name'

type Form = UseFormReturn<GenerationModifierForm>

interface SectionProps {
  form: Form
  shown: Set<string>
  onShow(key: string): void
  onHide(key: string): void
}

function useRowTexts() {
  return useTranslate({
    conditions: 'settings.generation-modifiers.conditions',
    effects: 'settings.generation-modifiers.effects',
    addCondition: 'settings.generation-modifiers.add-condition',
    addEffect: 'settings.generation-modifiers.add-effect',
    noConditions: 'settings.generation-modifiers.no-conditions-yet',
    noEffects: 'settings.generation-modifiers.no-effects-yet',
    remove: 'common.remove',
    condition: {
      location: 'settings.generation-modifiers.condition-location',
      restTypes: 'settings.generation-modifiers.rest-types',
      minDuration: 'settings.generation-modifiers.condition-min-duration',
      maxDuration: 'settings.generation-modifiers.condition-max-duration',
      salaryWorkTime: 'settings.generation-modifiers.salary-work-time',
      users: 'settings.generation-modifiers.users',
      validFrom: 'settings.generation-modifiers.valid-from',
      validTo: 'settings.generation-modifiers.valid-to',
    },
    effect: {
      award: 'settings.generation-modifiers.effect-award',
      amount: 'settings.generation-rules.amount',
      salaryCode: 'settings.generation-modifiers.salary-code',
      chainAction: 'settings.generation-modifiers.chain-action',
    },
    hours: 'settings.generation-modifiers.hours-unit',
    perOptional: 'settings.generation-modifiers.per-hours-optional',
    maxOptional: 'settings.generation-modifiers.max-optional',
    zones: 'settings.generation-modifiers.zones',
    countries: 'settings.generation-modifiers.countries',
    zonePlaceholder: 'settings.generation-modifiers.zone-placeholder',
    countryPlaceholder: 'settings.generation-modifiers.country-placeholder',
    fleetRequired: 'settings.generation-modifiers.fleet-required',
    salaryWorkTimePlaceholder:
      'settings.generation-modifiers.salary-work-time-placeholder',
    amountIgnoredOnChains: 'settings.generation-modifiers.amount-description',
    restAwardAmount: 'settings.generation-modifiers.error-rest-award-amount',
    chainOnly: 'settings.generation-modifiers.chain-action-description',
  })
}

export const ModifierConditions: React.FC<SectionProps> = ({
  form,
  shown,
  onShow,
  onHide,
}) => {
  const texts = useRowTexts()
  const values = useWatch({ control: form.control }) as GenerationModifierForm
  const visible = CONDITION_KEYS.filter(
    key =>
      conditionAvailable(key, values) &&
      (shown.has(key) || conditionIsSet(key, values))
  )
  const addable = CONDITION_KEYS.filter(
    key => conditionAvailable(key, values) && !visible.includes(key)
  )

  function remove(key: ConditionKey) {
    setValues(form, clearedCondition(key))
    onHide(key)
  }

  return (
    <BuilderSection
      title={texts.conditions}
      empty={texts.noConditions}
      error={form.formState.errors.root?.conditions?.message}
      addLabel={texts.addCondition}
      options={addable.map(key => ({ key, label: texts.condition[key] }))}
      onAdd={onShow}
    >
      {visible.map(key => (
        <BuilderRow
          key={key}
          label={texts.condition[key]}
          removeLabel={texts.remove}
          onRemove={() => remove(key)}
        >
          <ConditionEditor conditionKey={key} control={form.control} />
        </BuilderRow>
      ))}
    </BuilderSection>
  )
}

export const ModifierEffects: React.FC<SectionProps> = ({
  form,
  shown,
  onShow,
  onHide,
}) => {
  const texts = useRowTexts()
  const values = useWatch({ control: form.control }) as GenerationModifierForm
  const visible = EFFECT_KEYS.filter(
    key =>
      effectAvailable(key, values) &&
      (shown.has(key) ||
        effectIsSet(key, values) ||
        (key === 'amount' && amountRequired(values)))
  )
  const addable = EFFECT_KEYS.filter(
    key => effectAvailable(key, values) && !visible.includes(key)
  )

  function add(key: string) {
    setValues(form, addedEffect(key as EffectKey, values))
    onShow(key)
  }

  function remove(key: EffectKey) {
    setValues(form, clearedEffect(key))
    onHide(key)
  }

  return (
    <BuilderSection
      title={texts.effects}
      empty={texts.noEffects}
      error={form.formState.errors.root?.effects?.message}
      addLabel={texts.addEffect}
      options={addable.map(key => ({ key, label: texts.effect[key] }))}
      onAdd={add}
    >
      {visible.map(key => (
        <BuilderRow
          key={key}
          label={texts.effect[key]}
          removeLabel={texts.remove}
          onRemove={() => remove(key)}
        >
          <EffectEditor effectKey={key} control={form.control} />
        </BuilderRow>
      ))}
    </BuilderSection>
  )
}

function setValues(form: Form, values: Partial<GenerationModifierForm>) {
  for (const [name, value] of Object.entries(values))
    form.setValue(name as keyof GenerationModifierForm, value as never, {
      shouldDirty: true,
    })
}

const BuilderSection: React.FC<{
  title: string
  empty: string
  error?: string
  addLabel: string
  options: { key: string; label: string }[]
  onAdd(key: string): void
  children: ReactNode[]
}> = ({ title, empty, error, addLabel, options, onAdd, children }) => (
  <section className="flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <h4 className="font-semibold">{title}</h4>
      {options.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <FAIcon icon="plus" />
              {addLabel}
            </Button>
          </DropdownMenuTrigger>
          {/* Above the dialog (z-7000), level with popovers. */}
          <DropdownMenuContent align="end" className="z-9000">
            {options.map(option => (
              <DropdownMenuItem
                key={option.key}
                onClick={() => onAdd(option.key)}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
    {children.length === 0 && (
      <p className="text-secondary rounded-lg border border-dashed p-3 text-sm">
        {empty}
      </p>
    )}
    {children}
    {error && <p className="text-sm text-red-600">{error}</p>}
  </section>
)

const BuilderRow: React.FC<{
  label: string
  removeLabel: string
  onRemove(): void
  children: ReactNode
}> = ({ label, removeLabel, onRemove, children }) => (
  <div className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-start">
    <span className="text-sm font-medium sm:w-36 sm:shrink-0 sm:pt-2">
      {label}
    </span>
    <div className="min-w-0 flex-1">{children}</div>
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={removeLabel}
      className="self-end sm:self-start"
      onClick={onRemove}
    >
      <FAIcon icon="xmark" />
    </Button>
  </div>
)

const HoursInput: React.FC<{
  control: Control<GenerationModifierForm>
  name: 'minDurationHours' | 'maxDurationHours' | 'perDurationHours'
  unit: string
}> = ({ control, name, unit }) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <NumberInput
            className="w-28"
            value={field.value ?? undefined}
            decimalScale={2}
            onChange={value =>
              field.onChange(value === undefined ? null : value)
            }
          />
          <span className="text-sm">{unit}</span>
        </div>
        <FieldError errors={[fieldState.error]} />
      </div>
    )}
  />
)

const ConditionEditor: React.FC<{
  conditionKey: ConditionKey
  control: Control<GenerationModifierForm>
}> = ({ conditionKey, control }) => {
  const texts = useRowTexts()
  const labels = useGenerationRuleLabels()

  switch (conditionKey) {
    case 'location':
      return <LocationEditor control={control} />
    case 'restTypes':
      return (
        <Controller
          control={control}
          name="restTypes"
          render={({ field }) => (
            <div className="flex flex-wrap gap-4 pt-2">
              {REST_TYPES.map(restType => (
                <label
                  key={restType}
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    size="md"
                    value={field.value.includes(restType)}
                    onChange={checked =>
                      field.onChange(
                        checked
                          ? [...field.value, restType]
                          : field.value.filter(value => value !== restType)
                      )
                    }
                  />
                  {labels.restType[restType]}
                </label>
              ))}
            </div>
          )}
        />
      )
    case 'minDuration':
      return (
        <HoursInput
          control={control}
          name="minDurationHours"
          unit={texts.hours}
        />
      )
    case 'maxDuration':
      return (
        <HoursInput
          control={control}
          name="maxDurationHours"
          unit={texts.hours}
        />
      )
    case 'salaryWorkTime':
      return (
        <Controller
          control={control}
          name="salaryWorkTime"
          render={({ field }) => (
            <SalarySettingCombobox
              mode="single"
              value={field.value}
              placeholder={texts.salaryWorkTimePlaceholder}
              onChange={field.onChange}
            />
          )}
        />
      )
    case 'users':
      return (
        <Controller
          control={control}
          name="users"
          render={({ field }) => (
            <UserCombobox
              mode="multiple"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      )
    case 'validFrom':
    case 'validTo':
      return (
        <Controller
          control={control}
          name={conditionKey}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1">
              <DatePicker
                className="w-full"
                mode="single"
                value={field.value}
                onChange={field.onChange}
              />
              <FieldError errors={[fieldState.error]} />
            </div>
          )}
        />
      )
  }
}

const LocationEditor: React.FC<{
  control: Control<GenerationModifierForm>
}> = ({ control }) => {
  const texts = useRowTexts()
  const labels = useGenerationRuleLabels()
  const { moduleActive: fleetActive } = useModuleConfig(Module.FLEET)
  const { data } = useQuery(SALARY_ITEM_GENERATION_AREAS_QUERY)
  const zoneItems = (data?.salaryItemGenerationZones ?? []).map(zone => ({
    value: zone.id,
    label: zone.name,
  }))
  const countryItems = (data?.salaryItemGenerationCountries ?? [])
    .map(country => ({
      value: country.id,
      label: countryName(country.isoCode, country.name),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const matchModes = (
    ['starts_or_ends_in', 'starts_in', 'ends_in', 'starts_and_ends_in'] as const
  ).map(value => ({ value, label: labels.matchMode[value] }))
  const locationTests = (['inside', 'outside'] as const).map(value => ({
    value,
    label: labels.locationTest[value],
  }))

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Controller
          control={control}
          name="matchMode"
          render={({ field }) => (
            <Combobox
              className="w-48"
              mode="single"
              noSearch
              value={field.value}
              items={matchModes}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="locationTest"
          render={({ field }) => (
            <Combobox
              className="w-32"
              mode="single"
              noSearch
              value={field.value}
              items={locationTests}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="zones"
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <FieldLabel>{texts.zones}</FieldLabel>
            <Combobox
              mode="multiple"
              value={field.value}
              items={zoneItems}
              placeholder={texts.zonePlaceholder}
              onChange={field.onChange}
            />
          </div>
        )}
      />
      <Controller
        control={control}
        name="countries"
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <FieldLabel>{texts.countries}</FieldLabel>
            <Combobox
              mode="multiple"
              value={field.value}
              items={countryItems}
              placeholder={texts.countryPlaceholder}
              onChange={field.onChange}
            />
          </div>
        )}
      />
      {!fleetActive && (
        <p className="text-secondary text-sm">{texts.fleetRequired}</p>
      )}
    </div>
  )
}

const EffectEditor: React.FC<{
  effectKey: EffectKey
  control: Control<GenerationModifierForm>
}> = ({ effectKey, control }) => {
  const texts = useRowTexts()
  const labels = useGenerationRuleLabels()
  const values = useWatch({ control }) as GenerationModifierForm

  switch (effectKey) {
    case 'award':
      return (
        <Controller
          control={control}
          name="award"
          render={({ field }) => (
            <Combobox
              className="w-48"
              mode="single"
              noSearch
              value={field.value === 'none' ? 'deny' : field.value}
              items={(['award', 'deny'] as const).map(value => ({
                value,
                label: labels.award[value],
              }))}
              onChange={field.onChange}
            />
          )}
        />
      )
    case 'amount':
      return (
        <div className="flex flex-col gap-2">
          <Controller
            control={control}
            name="amount"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <NumberInput
                  className="w-28"
                  value={field.value ?? undefined}
                  decimalScale={3}
                  onChange={value =>
                    field.onChange(value === undefined ? null : value)
                  }
                />
                <FieldError errors={[fieldState.error]} />
                {!fieldState.error &&
                  field.value === null &&
                  amountRequired(values) && (
                    <p className="text-sm text-red-600">
                      {texts.restAwardAmount}
                    </p>
                  )}
              </div>
            )}
          />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>{texts.perOptional}</span>
            <HoursInput
              control={control}
              name="perDurationHours"
              unit={texts.hours}
            />
            {!!values.perDurationHours && (
              <Controller
                control={control}
                name="rounding"
                render={({ field }) => (
                  <Combobox
                    className="w-40"
                    mode="single"
                    noSearch
                    value={field.value}
                    items={(['ceil', 'floor'] as const).map(value => ({
                      value,
                      label: labels.rounding[value],
                    }))}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>{texts.maxOptional}</span>
            <Controller
              control={control}
              name="maxAmount"
              render={({ field }) => (
                <NumberInput
                  className="w-28"
                  value={field.value ?? undefined}
                  decimalScale={3}
                  onChange={value =>
                    field.onChange(value === undefined ? null : value)
                  }
                />
              )}
            />
          </div>
          <p className="text-secondary text-sm">
            {texts.amountIgnoredOnChains}
          </p>
        </div>
      )
    case 'salaryCode':
      return (
        <Controller
          control={control}
          name="salaryCode"
          render={({ field }) => (
            <SalaryCodeCombobox
              mode="single"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      )
    case 'chainAction':
      return (
        <div className="flex flex-col gap-1">
          <Controller
            control={control}
            name="chainAction"
            render={({ field }) => (
              <Combobox
                className="w-48"
                mode="single"
                noSearch
                value={field.value === 'none' ? 'break' : field.value}
                items={(values.appliesTo === 'work_period'
                  ? (['break'] as const)
                  : (['break', 'continue'] as const)
                ).map(value => ({ value, label: labels.chainAction[value] }))}
                onChange={field.onChange}
              />
            )}
          />
          <p className="text-secondary text-sm">{texts.chainOnly}</p>
        </div>
      )
  }
}
